import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { dateTime } from '@/lib/format'

/**
 * Устройства зала: экраны кухни и бара. Заводятся и здесь, и на кассе — список
 * общий (двусторонний синк по uid, LWW). Экран получает заказы своей секции
 * после того, как на нём введут код привязки.
 */

type DeviceKind = 'kitchen_screen' | 'bar_screen'

const KIND_LABEL: Record<DeviceKind, string> = {
  kitchen_screen: 'Экран кухни',
  bar_screen: 'Экран бара'
}

interface VenueDevice {
  id: string
  name: string
  kind: DeviceKind
  /** Код для планшета в одной сети с кассой (6 цифр). */
  pin: string
  /** Код облачного экрана (tracker.8ballpos.by), 8 символов — показываем XXXX-XXXX. */
  pairCode: string | null
  isActive: boolean
  deliveryEnabled: boolean
  createdAt: number
  lastSeenAt: number | null
}

export function Devices(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Устройства"
        lead="Экраны кухни и бара. Заведите экран здесь или на кассе, откройте на планшете адрес кассы и введите код привязки — экран начнёт получать заказы своей секции."
      />
      <ClubGate>
        <DevicesTable />
      </ClubGate>
    </>
  )
}

function DevicesTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<VenueDevice[]>('/v1/venue-devices', query, [query?.clubId])
  const [editing, setEditing] = useState<VenueDevice | 'new' | null>(null)
  const [deleting, setDeleting] = useState<VenueDevice | null>(null)
  const act = useMutate()
  const del = useMutate()

  const rows = list.data ?? []

  const onToggle = async (d: VenueDevice): Promise<void> => {
    const ok = await act.mutate(() =>
      api.patch(`/v1/venue-devices/${d.id}`, { isActive: !d.isActive }, { query })
    )
    if (ok) list.reload()
  }

  const onRegenerate = async (d: VenueDevice): Promise<void> => {
    const ok = await act.mutate(() => api.post(`/v1/venue-devices/${d.id}/pin`, {}, { query }))
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/venue-devices/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} устройств(а)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Добавить экран
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Экранов пока нет. Добавьте экран и введите его код на планшете."
      />
      {act.error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {act.error}
        </div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Устройство</th>
                <th>Функция</th>
                <th>Код в этой сети</th>
                <th>Код через интернет</th>
                <th>Доставка</th>
                <th>На связи</th>
                <th>Статус</th>
                <th style={{ width: 200 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.name}</td>
                  <td>{KIND_LABEL[d.kind] ?? d.kind}</td>
                  <td className="mono" style={{ letterSpacing: 2 }}>
                    {d.pin}
                  </td>
                  {/* Разные коды и разные адреса: 6 цифр — для планшета в локальной
                      сети, XXXX-XXXX — для tracker.8ballpos.by. Их путали. */}
                  <td className="mono" style={{ letterSpacing: 2 }}>
                    {d.pairCode ? (
                      `${d.pairCode.slice(0, 4)}-${d.pairCode.slice(4)}`
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className={d.deliveryEnabled ? '' : 'muted'}>
                    {d.deliveryEnabled ? '✓' : '—'}
                  </td>
                  <td className="muted">
                    {d.lastSeenAt ? dateTime(d.lastSeenAt) : 'ещё не подключался'}
                  </td>
                  <td>
                    <span className={d.isActive ? 'badge' : 'muted'}>
                      {d.isActive ? 'Включён' : 'Выключен'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={act.saving}
                        title="Выдать новый код — экран придётся привязать заново"
                        onClick={() => void onRegenerate(d)}
                      >
                        Новый код
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={act.saving}
                        onClick={() => void onToggle(d)}
                      >
                        {d.isActive ? 'Выключить' : 'Включить'}
                      </Button>
                      <IconButton
                        title={`Изменить «${d.name}»`}
                        aria-label="Изменить устройство"
                        onClick={() => setEditing(d)}
                      >
                        {Icon.edit}
                      </IconButton>
                      <IconButton
                        danger
                        title={`Удалить «${d.name}»`}
                        aria-label="Удалить устройство"
                        onClick={() => setDeleting(d)}
                      >
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <DeviceForm
          device={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить устройство"
          message={`Удалить «${deleting.name}»? Экран с этим кодом перестанет получать заказы.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function DeviceForm({
  device,
  query,
  onClose,
  onSaved
}: {
  device: VenueDevice | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = device !== null
  const [name, setName] = useState(device?.name ?? '')
  const [kind, setKind] = useState<DeviceKind>(device?.kind ?? 'kitchen_screen')
  const [deliveryEnabled, setDeliveryEnabled] = useState(device?.deliveryEnabled ?? false)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!name.trim()) return setValidation('Опишите, что это за устройство и где оно стоит.')
    setValidation(null)
    const body = { name: name.trim(), kind, deliveryEnabled }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/venue-devices/${device.id}`, body, { query })
        : api.post('/v1/venue-devices', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить устройство' : 'Добавление устройства'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="device-form">
            {isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
        </>
      }
    >
      <form id="device-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field
            label="Что за устройство и где находится?"
            htmlFor="d-name"
            hint="Например: планшет на раздаче, ТВ у барной стойки"
            full
          >
            <Input
              id="d-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Планшет на раздаче"
              autoFocus
            />
          </Field>
          <Field
            label="Какую функцию выполняет?"
            htmlFor="d-kind"
            hint="Экран получает заказы только своей секции"
            full
          >
            <Select
              id="d-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as DeviceKind)}
            >
              <option value="kitchen_screen">{KIND_LABEL.kitchen_screen}</option>
              <option value="bar_screen">{KIND_LABEL.bar_screen}</option>
            </Select>
          </Field>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={deliveryEnabled}
                onChange={(e) => setDeliveryEnabled(e.target.checked)}
              />
              Доставка
            </label>
            <span className="field__hint">
              С включённой доставкой экран может отмечать готовые заказы переданными в доставку.
              Кнопка появляется только на экранах с этим тумблером.
            </span>
          </div>
        </div>
        {!isEdit && (
          <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            Код привязки сгенерируется автоматически — он появится в списке, его вводят один раз на
            самом экране.
          </p>
        )}
        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
